import { Card, CardProps } from "@imminently/react/ui/core/card";
import { Text } from "@imminently/react/ui/core/text";
import { Box, Stack, Tooltip } from "@mui/material";
import { PatientEncounter } from "@/resources/types";
import { Theme, css } from "@/theme";
import { useEncounterCommentModal } from "@/modals/encounterComment";

const COMMENT_TRUNCATE_LEN = 40;

const getCommentText = (comment: PatientEncounter["comment"]): string =>
  comment?.text ?? "";

const getCommentSeverity = (comment: PatientEncounter["comment"]): "low" | "medium" | "high" =>
  comment?.severity === "high" || comment?.severity === "medium" ? comment.severity : "low";

const getCommentModifiedBy = (comment: PatientEncounter["comment"]): string | undefined =>
  comment?.modifiedBy?.name;

const styles = {
  card: (theme: Theme) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0.5rem 1rem;
    min-width: 10rem;
    max-width: 16rem;
    box-shadow: none;
    cursor: pointer;
    border-radius: 0.5rem;
    gap: 0.5rem;
    transition: all 0.2s ease;
    /* background and border set via sx for severity-based colours */

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
  `,
};

type PatientEncounterCommentProps = CardProps & {
  encounter: PatientEncounter;
};

const PatientEncounterComment = ({ encounter, ...props }: PatientEncounterCommentProps) => {
  const openCommentModal = useEncounterCommentModal();
  const commentText = getCommentText(encounter.comment);
  const severity = getCommentSeverity(encounter.comment);
  const modifiedBy = getCommentModifiedBy(encounter.comment);

  const handleClick = () => {
    openCommentModal({ encounter });
  };

  const displayText = commentText
    ? commentText.length > COMMENT_TRUNCATE_LEN
      ? `${commentText.slice(0, COMMENT_TRUNCATE_LEN)}...`
      : commentText
    : "Add Comment";

  const tooltipTitle = commentText
    ? (modifiedBy ? (
        <Stack spacing={0.5} sx={{ py: 0.5 }}>
          <span>{commentText}</span>
          <Text variant="caption" color="text.secondary">— {modifiedBy}</Text>
        </Stack>
      ) : commentText)
    : undefined;
  const bgColor = severity === "high" ? "error.100" : severity === "medium" ? "warning.100" : "grey.100";
  const borderColor = severity === "high" ? "error.300" : severity === "medium" ? "warning.300" : "grey.300";

  return (
    <Card
      {...props}
      css={styles.card}
      onClick={handleClick}
      sx={{
        backgroundColor: bgColor,
        border: "1px solid",
        borderColor,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        {commentText ? (
          <Tooltip title={tooltipTitle} placement="top" arrow>
            <Text variant="body2" noWrap>
              {displayText}
            </Text>
          </Tooltip>
        ) : (
          <Text variant="body2" color="text.secondary">
            {displayText}
          </Text>
        )}
      </Box>
    </Card>
  );
};

export default PatientEncounterComment;
